import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { identifyUser, resetUser, captureEvent } from '../lib/analytics';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Configure axios defaults - Dynamic API URL
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Dynamic detection based on current domain
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:3000';
  } else {
    return `https://${hostname}`;
  }
};

axios.defaults.baseURL = `${getApiBaseUrl()}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Set up axios interceptor for authentication
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid
          logout();
          toast.error('Session expired. Please log in again.');
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [token]);

  // Load user on app start
  useEffect(() => {
    const loadUser = async () => {
      if (token) {
        try {
          const response = await axios.get('/auth/profile');
          setUser(response.data.user);
          // Identify to PostHog on session restore too — otherwise returning,
          // already-logged-in visitors (who never hit the login() path this
          // session) stay anonymous and can't be distinguished in analytics.
          identifyUser(response.data.user);
        } catch (error) {
          console.error('Error loading user:', error);
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };

    loadUser();
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await axios.post('/auth/login', { email, password });
      const { token: newToken, user: userData } = response.data;
      
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      
      identifyUser(userData);
      captureEvent('login', { method: 'password' });
      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      const code = error.response?.data?.code || null;
      toast.error(message);
      return { success: false, error: message, code };
    }
  };

  // Social / OAuth login. `payload` is provider-specific:
  //   OIDC providers (google, microsoft, facebook, amazon): { credential }
  //   code-flow providers (github, linkedin, twitter): { code, redirectUri, codeVerifier? }
  const loginWithProvider = async (provider, payload) => {
    try {
      const response = await axios.post('/auth/oauth', { provider, ...payload });
      const { token: newToken, user: userData } = response.data;

      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);

      identifyUser(userData);
      captureEvent('login', { method: 'oauth', provider });
      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Social login failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post('/auth/register', userData);
      const data = response.data || {};

      // New flow: registration no longer auto-logs-in. The account is created
      // PENDING and a verification email is sent; the user must click the link.
      if (data.requiresVerification) {
        captureEvent('registration_started', { email: data.email });
        toast.success('Check your email to verify your account.');
        return { success: true, requiresVerification: true, email: data.email };
      }

      // Backward-compatible fallback (e.g. if a token is ever returned).
      if (data.token) {
        localStorage.setItem('token', data.token);
        setToken(data.token);
        setUser(data.user);
        identifyUser(data.user);
        captureEvent('registration_completed', { method: 'password' });
        toast.success('Registration successful!');
        return { success: true };
      }
      return { success: true, requiresVerification: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Verify a newly-registered email via the token from the emailed link.
  // On success the backend returns a login JWT, so we sign the user in.
  const verifyEmail = async (token) => {
    try {
      const response = await axios.post('/auth/verify-email', { token });
      const { token: newToken, user: newUser } = response.data || {};
      if (newToken) {
        localStorage.setItem('token', newToken);
        setToken(newToken);
        setUser(newUser);
        identifyUser(newUser);
        captureEvent('email_verified');
      }
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Verification failed';
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    captureEvent('logout');
    resetUser();
    toast.success('Logged out successfully');
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await axios.put('/auth/profile', profileData);
      setUser(response.data.user);
      toast.success('Profile updated successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Profile update failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await axios.post('/auth/change-password', {
        email: user?.email,
        currentPassword,
        newPassword,
      });
      toast.success('Password changed successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Password change failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Passwordless login with an emailed one-time code. Mirrors login(): on success
  // stores the JWT + user and marks the session authenticated.
  const loginWithPasscode = async (email, passcode) => {
    try {
      const response = await axios.post('/auth/passcode-login', { email, passcode });
      const { token: newToken, user: userData } = response.data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      identifyUser(userData);
      captureEvent('login', { method: 'passcode' });
      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'That code is invalid or has expired.';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const refreshToken = async () => {
    try {
      const response = await axios.post('/auth/refresh-token');
      const newToken = response.data.token;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      return { success: true };
    } catch (error) {
      logout();
      return { success: false };
    }
  };

  // Helper functions for subscription checks
  const hasActiveSubscription = () => {
    return user?.subscription?.status === 'active';
  };

  const hasPremiumAccess = () => {
    return hasActiveSubscription() && 
           ['premium', 'professional'].includes(user?.subscription?.plan);
  };

  const hasProfessionalAccess = () => {
    return hasActiveSubscription() && 
           user?.subscription?.plan === 'professional';
  };

  const getCurrentPlan = () => {
    return user?.subscription?.plan || 'free';
  };

  const updateUserSubscription = (subscriptionData) => {
    setUser(prevUser => ({
      ...prevUser,
      subscription: {
        ...prevUser.subscription,
        ...subscriptionData
      }
    }));
  };

  const value = {
    user,
    loading,
    token,
    login,
    loginWithProvider,
    loginWithPasscode,
    register,
    verifyEmail,
    logout,
    updateProfile,
    changePassword,
    refreshToken,
    hasActiveSubscription,
    hasPremiumAccess,
    hasProfessionalAccess,
    getCurrentPlan,
    updateUserSubscription
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
